import React, { useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { apiService, SimilarProduct } from '../services/api';
import StockBadge, { stockSummary } from '../components/StockBadge';

const fmtPct = (v: number | null): string => {
  if (v === null || v === undefined) return '-';
  return (v * 100).toFixed(1) + '%';
};

const fmtEur = (v: number | null | undefined): string => {
  if (v === null || v === undefined) return '-';
  return v.toLocaleString('pt-PT', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €';
};

const simBadge = (s: number | null): JSX.Element => {
  if (s === null || s === undefined) return <span className="text-muted">-</span>;
  let cls = 'badge bg-secondary';
  if (s >= 0.8) cls = 'badge bg-success';
  else if (s >= 0.6) cls = 'badge bg-primary';
  else if (s >= 0.45) cls = 'badge bg-info text-dark';
  return <span className={cls}>{(s * 100).toFixed(1)}%</span>;
};

const downloadCsv = (sku: string, rows: SimilarProduct[]) => {
  const header = ['neighbor_sku', 'neighbor_name', 'neighbor_category', 'neighbor_brand', 'neighbor_price', 'similarity', 'same_category', 'rank', 'stock'];
  const fmtText = (v: any): string => {
    if (v === null || v === undefined) return '';
    const s = String(v);
    return /[;"\r\n]/.test(s) ? '"' + s.replace(/"/g, '""') + '"' : s;
  };
  const fmtFloat = (v: number | null | undefined): string => (v === null || v === undefined ? '' : String(v).replace('.', ','));
  const lines = rows.map((r) => [
    fmtText(r.neighbor_sku), fmtText(r.neighbor_name), fmtText(r.neighbor_category),
    fmtText(r.neighbor_brand), fmtFloat(r.neighbor_price), fmtFloat(r.similarity), r.same_category ? 'sim' : 'nao', r.rank,
    r.neighbor_in_stock === undefined ? '' : (r.neighbor_in_stock ? 'com stock' : 'sem stock'),
  ].join(';'));
  const csv = [header.join(';'), ...lines].join('\r\n');
  const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `similares_${sku}.csv`;
  a.click();
  URL.revokeObjectURL(url);
};

const SemanticSearchPage: React.FC = () => {
  const [sku, setSku] = useState('');
  const [submittedSku, setSubmittedSku] = useState('');
  const [submittedName, setSubmittedName] = useState<string | null>(null);
  const [submittedCategory, setSubmittedCategory] = useState<string | null>(null);
  const [submittedPrice, setSubmittedPrice] = useState<number | null>(null);
  const [submittedInStock, setSubmittedInStock] = useState<boolean | null>(null);
  const [results, setResults] = useState<SimilarProduct[]>([]);
  const [inStockOnly, setInStockOnly] = useState(true);
  const [sameCategory, setSameCategory] = useState(false);
  const [excludeSameBrand, setExcludeSameBrand] = useState(false);
  const [maxPerBrand, setMaxPerBrand] = useState(0);
  const [priceUpPct, setPriceUpPct] = useState(0);
  const [priceDownPct, setPriceDownPct] = useState(0);
  const [limit, setLimit] = useState(20);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const requestRef = useRef(0);

  const handleSearch = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    const trimmed = sku.trim();
    if (!trimmed) return;
    const reqId = ++requestRef.current;
    setLoading(true);
    setError(null);
    setSubmittedSku(trimmed);
    try {
      const resp = await apiService.getSimilarProducts(trimmed, {
        same_category: sameCategory,
        exclude_same_brand: excludeSameBrand,
        max_per_brand: maxPerBrand,
        price_up_pct: priceUpPct,
        price_down_pct: priceDownPct,
        limit,
        in_stock_only: inStockOnly,
      });
      if (reqId !== requestRef.current) return;
      setResults(resp.results);
      setSubmittedName(resp.sku_name ?? null);
      setSubmittedCategory(resp.sku_category ?? null);
      setSubmittedPrice(resp.sku_price ?? null);
      setSubmittedInStock(resp.sku_in_stock ?? null);
    } catch (err: any) {
      if (reqId !== requestRef.current) return;
      setError(err?.response?.data?.error || err?.message || 'Erro a carregar produtos semelhantes');
      setResults([]);
      setSubmittedName(null);
      setSubmittedCategory(null);
      setSubmittedPrice(null);
      setSubmittedInStock(null);
    } finally {
      if (reqId === requestRef.current) setLoading(false);
    }
  };

  return (
    <div className="container-fluid py-3">
      <div className="mb-3">
        <h2 className="mb-1">🧭 Produtos Semelhantes por Título</h2>
        <p className="text-muted small mb-0">
          Dado um SKU, mostra os produtos com <em>título mais parecido semanticamente</em> (embeddings
          multilingual + cosine). Útil para encontrar substitutos / variantes. Distinto da análise de
          cesto (produtos comprados em conjunto).
        </p>
      </div>

      <form onSubmit={handleSearch} className="row g-2 align-items-end mb-3">
        <div className="col-md-3">
          <label className="form-label small mb-1">SKU</label>
          <input
            type="text"
            className="form-control"
            placeholder="Ex: P016691"
            value={sku}
            onChange={(e) => setSku(e.target.value)}
          />
        </div>
        <div className="col-md-2">
          <label className="form-label small mb-1">Top N</label>
          <input
            type="number"
            className="form-control"
            min={1}
            value={limit}
            onChange={(e) => { const n = parseInt(e.target.value, 10); setLimit(Number.isFinite(n) ? n : 20); }}
          />
        </div>
        <div className="col-md-2">
          <label className="form-label small mb-1" title="0 = sem limite">Máx. por marca</label>
          <input
            type="number"
            className="form-control"
            min={0}
            max={50}
            value={maxPerBrand}
            onChange={(e) => { const n = parseInt(e.target.value, 10); setMaxPerBrand(Number.isFinite(n) ? n : 0); }}
          />
        </div>
        <div className="col-md-1">
          <label className="form-label small mb-1" title="0 = sem limite">Preço +%</label>
          <input
            type="number"
            className="form-control"
            min={0}
            value={priceUpPct}
            onChange={(e) => { const n = parseFloat(e.target.value); setPriceUpPct(Number.isFinite(n) ? n : 0); }}
          />
        </div>
        <div className="col-md-1">
          <label className="form-label small mb-1" title="0 = sem limite">Preço −%</label>
          <input
            type="number"
            className="form-control"
            min={0}
            value={priceDownPct}
            onChange={(e) => { const n = parseFloat(e.target.value); setPriceDownPct(Number.isFinite(n) ? n : 0); }}
          />
        </div>
        <div className="col-md-3">
          <div className="form-check">
            <input
              className="form-check-input"
              type="checkbox"
              id="inStockOnly"
              checked={inStockOnly}
              onChange={(e) => setInStockOnly(e.target.checked)}
            />
            <label className="form-check-label small" htmlFor="inStockOnly" title="Stock do site (Saleor), actualizado antes de cada ranking">
              Só com stock
            </label>
          </div>
          <div className="form-check">
            <input
              className="form-check-input"
              type="checkbox"
              id="sameCategory"
              checked={sameCategory}
              onChange={(e) => setSameCategory(e.target.checked)}
            />
            <label className="form-check-label small" htmlFor="sameCategory">
              Apenas mesma categoria
            </label>
          </div>
          <div className="form-check">
            <input
              className="form-check-input"
              type="checkbox"
              id="excludeSameBrand"
              checked={excludeSameBrand}
              onChange={(e) => setExcludeSameBrand(e.target.checked)}
            />
            <label className="form-check-label small" htmlFor="excludeSameBrand">
              Excluir mesma marca
            </label>
          </div>
        </div>
        <div className="col-md-2">
          <button type="submit" className="btn btn-primary w-100" disabled={loading || !sku.trim()}>
            {loading ? 'A procurar...' : 'Procurar'}
          </button>
        </div>
      </form>

      {error && <div className="alert alert-danger">{error}</div>}

      {submittedSku && !loading && !error && (
        <div className="mb-2 d-flex justify-content-between align-items-end">
          <div>
            <h5 className="mb-1">
              <span className="fw-bold">{submittedSku}</span>
              {submittedName ? <span className="text-muted fw-normal"> · {submittedName}</span> : null}
              {submittedPrice !== null ? <span className="badge bg-dark ms-2">{fmtEur(submittedPrice)}</span> : null}
              <StockBadge inStock={submittedInStock} className="ms-2" />
            </h5>
            <p className="text-muted small mb-0">
              {submittedCategory ? <span>Categoria: <strong>{submittedCategory}</strong> · </span> : null}
              {(priceUpPct > 0 || priceDownPct > 0) && submittedPrice !== null ? (
                <span>Banda preço: <strong>{fmtEur(submittedPrice * (1 - priceDownPct / 100))} – {fmtEur(submittedPrice * (1 + priceUpPct / 100))}</strong> · </span>
              ) : null}
              {results.length === 0
                ? 'Sem produtos semelhantes.'
                : `${results.length} produto(s) semelhante(s)${sameCategory ? ' na mesma categoria' : ''} (${stockSummary(results.map((r) => r.neighbor_in_stock))}).`}
            </p>
          </div>
          {results.length > 0 && (
            <button className="btn btn-outline-secondary btn-sm" onClick={() => downloadCsv(submittedSku, results)}>
              CSV
            </button>
          )}
        </div>
      )}

      {results.length > 0 && (
        <div className="table-responsive">
          <table className="table table-sm table-hover align-middle">
            <thead className="table-light">
              <tr>
                <th>#</th>
                <th>Produto semelhante</th>
                <th>Categoria</th>
                <th>Marca</th>
                <th className="text-end">Preço</th>
                <th>Stock</th>
                <th className="text-end">Similaridade</th>
              </tr>
            </thead>
            <tbody>
              {results.map((r, idx) => (
                <tr key={r.neighbor_sku}>
                  <td className="text-muted">{idx + 1}</td>
                  <td>
                    <Link to={`/products/${r.neighbor_sku}`} className="text-decoration-none">
                      <span className="fw-semibold">{r.neighbor_sku}</span>
                      {r.neighbor_name ? (
                        <span className="text-muted ms-1">· {r.neighbor_name}</span>
                      ) : null}
                    </Link>
                  </td>
                  <td className="small">
                    {r.neighbor_category || '-'}
                    {r.same_category === false ? <span className="badge bg-light text-muted ms-1">≠ cat</span> : null}
                  </td>
                  <td className="small">{r.neighbor_brand || '-'}</td>
                  <td className="text-end">{fmtEur(r.neighbor_price)}</td>
                  <td><StockBadge inStock={r.neighbor_in_stock} /></td>
                  <td className="text-end">{simBadge(r.similarity)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="alert alert-warning mt-3 small">
        <strong>Nota:</strong> a semelhança é só ao nível do <em>título</em>. Acessórios podem aparecer
        próximos do produto principal (ex: "Capa para Portátil X"). Usar o filtro <em>mesma categoria</em>
        para isolar substitutos genuínos.
      </div>
    </div>
  );
};

export default SemanticSearchPage;
